<script>
	import BrainIcon from 'components/svg/brain-icon.vue';
	import OpenBookIcon from 'components/svg/open-book-icon.vue';
	import LightbulbGearIcon from 'components/svg/lightbulb-gear-icon.vue';
	import OutboundLink from 'components/outbound-link.vue';
	import analyticsUtilities from 'utilities/analyticsUtilities';

	export default {
		components: {
			'brain-icon': BrainIcon,
			'open-book-icon': OpenBookIcon,
			'lightbulb-gear-icon': LightbulbGearIcon,
			'outbound-link': OutboundLink
		},
		data() {
			return {
				pillars: [
					{
						name: 'background-knowledge',
						link: '/teacher-guide/build-background-knowledge.html',
						text: 'Build Background Knowledge',
						icon: 'brain-icon',
						tag: 'pillar-background-knowledge'
					},
					{
						name: 'grow-vocabularies',
						link: '/teacher-guide/grow-vocabularies.html',
						text: 'Grow Vocabularies',
						icon: 'open-book-icon',
						tag: 'pillar-grow-vocabularies'
					},
					{
						name: 'strategic-reading',
						link: '/teacher-guide/develop-comprehension-strategies.html',
						text: 'Strengthen Strategic Reading',
						icon: 'lightbulb-gear-icon',
						tag: 'pillar-strategic-reading'
					}
				]
			};
		},
		methods: {
			homepageClick(loc) {
				analyticsUtilities.fireEvent(analyticsUtilities.events.HOMEPAGE_CLICK, {
					loc
				});
			}
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';
	@import 'mixins/media';

	.pillars-panel {
		padding: 3rem 0;
		background-color: $c-blue-accent-1;
		color: $c-white;

		h2 {
			text-align: center;
			margin-top: 0;
		}

		.pillars {
			display: grid;
			grid-template-columns: 1fr;
			gap: 1rem;
			justify-content: center;

			@include media(med) {
				grid-template-columns: repeat(3, 1fr);
				gap: 2rem;
				padding: 0 2rem;
			}

			@include media(lg) {
				gap: 4rem;
				padding: 0 4rem;
			}
		}

		.pillar {
			height: 100%;
			display: flex;
			flex-wrap: wrap;
			align-items: center;
			padding: 1rem;
			color: $c-white;
			transition:
				box-shadow 0.4s,
				background-image 0.8s;

			@include media(med) {
				padding: 1.25rem;
				flex-direction: column;
				align-items: flex-start;
				justify-content: space-between;
			}

			@include media(lg) {
				padding: 1.5rem;
			}

			&.background-knowledge {
				background: radial-gradient(circle at bottom right, rgba($c-blue, 0.8) 0, rgba($c-blue-accent-2, 1) 40%);

				&:hover {
					background: radial-gradient(circle at bottom right, rgba($c-blue, 0.8) 0, rgba($c-blue-accent-1, 1) 40%);
				}
			}

			&.grow-vocabularies {
				background: radial-gradient(circle at center right, rgba($c-blue-accent-4, 1) 0, rgba($c-blue-accent-3, 1) 50%);

				&:hover {
					background: radial-gradient(circle at center right, rgba($c-blue-accent-4, 1) 0, rgba($c-blue-accent-1, 1) 50%);
				}

				@include media(med) {
					background: radial-gradient(circle at 40% 100%, rgba($c-blue-dark-2, 1) 0, rgba($c-blue-accent-3, 1) 30%);

					&:hover {
						background: radial-gradient(circle at 40% 100%, rgba($c-blue-dark-2, 1) 0, rgba($c-blue-accent-1, 1) 30%);
					}
				}
			}

			&.strategic-reading {
				background: radial-gradient(circle at top right, rgba($c-blue, 1) 0, rgba($c-blue-accent-4, 1) 30%);

				&:hover {
					background: radial-gradient(circle at top right, rgba($c-blue, 1) 0, rgba($c-blue-accent-1, 1) 30%);
				}

				@include media(med) {
					background: radial-gradient(circle at 110% 35%, rgba($c-blue, 1) 0, rgba($c-blue-accent-4, 1) 40%);

					&:hover {
						background: radial-gradient(circle at 110% 35%, rgba($c-blue, 1) 0, rgba($c-blue-accent-1, 1) 40%);
					}
				}
			}

			&:hover {
				box-shadow: inset 0 0 0 2px $c-slate-light;
				color: $c-white;
			}
		}

		.pillar-heading {
			margin: 0;
			flex: 1 1;
			font-size: 1.35rem;

			@include media(med) {
				margin-bottom: 1rem;
				font-size: 1.65rem;
			}

			@include media(lg) {
				font-size: 1.875rem;
				line-height: 1.3;
			}
		}

		.pillar-icon {
			flex-basis: 5rem;
			margin-left: 1rem;

			@include media(med) {
				flex-basis: auto;
				margin-left: 0;
				max-height: 10rem;
				align-self: center;
			}

			path {
				fill: $c-white;
				fill-opacity: 0.5;
			}
		}

		.pillar-info {
			margin-top: 2rem;
			text-align: center;
		}

		.panel-content {
			a {
				color: $c-blue-light;
				text-decoration: underline;

				&:hover {
					color: $c-babyblue;
					text-decoration: none;
				}
			}
		}
	}
</style>
<template>
	<section class="pillars-panel spotlight-bg">
		<div class="wrapper">
			<h2>Pillars of Reading Comprehension</h2>
			<ul class="pillars list-unstyled">
				<li
					v-for="pillar in pillars"
					:key="pillar.name"
					class="pillar-wrapper">
					<a
						:class="['pillar', pillar.name]"
						:href="pillar.link"
						@click="homepageClick(pillar.tag)">
						<h3 class="pillar-heading">{{ pillar.text }}</h3>
						<component
							:is="pillar.icon"
							class="pillar-icon"></component>
					</a>
				</li>
			</ul>
			<div class="pillar-info panel-content">
				ReadWorks is built on the
				<outbound-link
					href="https://about.readworks.org/the-research.html"
					data-outbound-link="READWORKS_RESEARCH"
					>science of reading</outbound-link
				>
				to ensure students can steadily expand their background knowledge, vocabularies, and reading comprehension—helping them become the confident
				readers they deserve to be.
			</div>
		</div>
	</section>
</template>
